import React, { useState, useEffect } from 'react';
import { ownerAPI } from '../utils/api';
import { formatDistanceToNow } from 'date-fns';
import './VisitorLogs.css';

const ACTION_ICONS = {
  SCAN: { icon: '👁️', label: 'Door Scanned', color: 'var(--info)' },
  CALL_REQUEST: { icon: '📲', label: 'Call Requested', color: 'var(--accent)' },
  MESSAGE: { icon: '✉️', label: 'Message Left', color: 'var(--success)' },
  PACKAGE: { icon: '📦', label: 'Package Delivered', color: 'var(--warning)' },
  CALL_CONNECTED: { icon: '📞', label: 'Call Connected', color: 'var(--success)' },
  CALL_FAILED: { icon: '📵', label: 'Call Failed', color: 'var(--danger)' },
  CALL_DECLINED: { icon: '🚫', label: 'Call Declined', color: 'var(--danger)' },
};

const FILTERS = ['All', 'SCAN', 'CALL_REQUEST', 'MESSAGE', 'PACKAGE', 'CALL_CONNECTED'];

const VisitorLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({});

  const fetchLogs = async (p = 1, action = 'All') => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (action !== 'All') params.action = action;
      const res = await ownerAPI.getLogs(params);
      const data = res.data.data;
      setLogs(data.logs);
      setPagination(data.pagination);
      setStats(data.stats);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(1, filter); }, [filter]);

  const handleFilter = (f) => {
    setFilter(f);
    setPage(1);
  };

  const handlePage = (p) => {
    setPage(p);
    fetchLogs(p, filter);
  };

  return (
    <div className="vl-root animate-fade-in">
      {/* Stats summary */}
      <div className="vl-stats">
        <div className="vl-stat">
          <span className="vl-stat-value">{stats.today ?? 0}</span>
          <span className="vl-stat-label">Today</span>
        </div>
        <div className="vl-stat">
          <span className="vl-stat-value">{stats.week ?? 0}</span>
          <span className="vl-stat-label">This Week</span>
        </div>
        <div className="vl-stat">
          <span className="vl-stat-value">{stats.total ?? 0}</span>
          <span className="vl-stat-label">Total</span>
        </div>
      </div>

      {/* Filters */}
      <div className="vl-filters">
        {FILTERS.map(f => (
          <button
            key={f}
            className={`vl-filter-btn ${filter === f ? 'vl-filter-btn--active' : ''}`}
            onClick={() => handleFilter(f)}
          >
            {f === 'All' ? 'All' : (ACTION_ICONS[f]?.icon + ' ' + ACTION_ICONS[f]?.label)}
          </button>
        ))}
      </div>

      {/* Log list */}
      {loading ? (
        <div className="vl-loading">
          <div className="spinner" />
        </div>
      ) : logs.length === 0 ? (
        <div className="vl-empty">
          <span>📋</span>
          <p>No activity logs {filter !== 'All' ? `for "${filter}"` : 'yet'}.</p>
        </div>
      ) : (
        <div className="vl-list">
          {logs.map(log => (
            <LogEntry key={log._id} log={log} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="vl-pagination">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => handlePage(page - 1)}
            disabled={page <= 1}
          >
            ← Previous
          </button>
          <span className="vl-page-info">
            Page {page} of {pagination.totalPages}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => handlePage(page + 1)}
            disabled={!pagination.hasMore}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

const LogEntry = ({ log }) => {
  const meta = ACTION_ICONS[log.action] || { icon: '📌', label: log.action, color: 'var(--text-muted)' };

  return (
    <div className="vl-entry">
      <div className="vl-entry-icon" style={{ background: `color-mix(in srgb, ${meta.color} 12%, transparent)` }}>
        {meta.icon}
      </div>
      <div className="vl-entry-body">
        <div className="vl-entry-header">
          <span className="vl-entry-action" style={{ color: meta.color }}>{meta.label}</span>
          <span className="vl-entry-mode">
            {log.modeAtVisit === 'HOME' ? '🏠' : log.modeAtVisit === 'AWAY' ? '🧳' : '🔕'} {log.modeAtVisit}
          </span>
        </div>
        {log.content && (
          <p className="vl-entry-content">"{log.content}"</p>
        )}
        <div className="vl-entry-meta">
          <span>{formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}</span>
          <span>·</span>
          <span>{new Date(log.timestamp).toLocaleString('en-IN', {
            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
          })}</span>
        </div>
      </div>
    </div>
  );
};

export default VisitorLogs;
